import React, { Fragment, useEffect, useState, useTransition } from 'react'
import PropTypes from 'prop-types'
import { browserHistory, buildStudyRoutePath } from 'utils/navigation'
import { Button, Loader, SearchInput } from 'components/UIElements'
import { getUserRoleByStudy, isRootOrDchAdmin } from 'utils/misc'
import { getStudyLength } from 'utils/time'
import {
  USER_ROLES_MAP,
  TOOLTIP_MENU,
  TOOLTIP_MENU_MAP,
  MAX_STUDIES_ON_PAGE,
  STUDY_SORTING_OPTIONS,
  MODAL_CLASSES_MAP,
  MODAL_CONTENT_MAP,
  MODAL_BUTTONS_MAP,
} from 'utils/constants'
import STRINGS from 'utils/strings'
import { persistStudyPaginationSettings } from 'utils/studyConfigUtils'
import '../stylesheets/Studies.scss'
import { CREATE_STUDY_FULL_URLS } from '../../routes/CreateStudy/utils/studyUtils'
import StudiesPaginationPanel from './StudiesPaginationPanel'
import StudyFeatureFilter from './StudyFeatureFilter'
import StudySorting from './StudySorting'
import StudyCard from './StudyCard'
import SelectStudyVersionModalContainer from '../containers/SelectStudyVersionModalContainer'
import { runStudyListNavigation } from '../utils/studyCardNavigation'
import { DEFAULT_STUDIES_PAGE } from '../modules/Studies'

const searchImg = require('../assets/search.png')

const StudiesList = ({
  user,
  studiesPage = DEFAULT_STUDIES_PAGE,
  searchTerm,
  allowCreateStudy,
  openModal,
  closeModal,
  uploadLogo,
  deleteLogo,
  addPreviewLogo,
  resetStudyData,
  studyPaginationSettings,
  fetchStudyVersionList,
  fetchStudy,
  studyVersionList,
  fetchPaginatedStudies,
  isListLoading,
}) => {
  const [isPending, startTransition] = useTransition()
  const [currentPage, setCurrentPage] = useState(studyPaginationSettings?.currentPage || 1)
  const [selectedFilterFeatures, setSelectedFilterFeatures] = useState(
    studyPaginationSettings?.selectedFilterFeatures || [],
  )
  const [sortingOption, setSortingOption] = useState(
    studyPaginationSettings?.sortingOption || STUDY_SORTING_OPTIONS.studyCreateDate,
  )
  const [ascendingSortingOrder, setAscendingSortingOrder] = useState(
    studyPaginationSettings?.ascendingSortingOrder !== false,
  )

  const items = studiesPage?.items || []
  const pagination = studiesPage?.pagination || DEFAULT_STUDIES_PAGE.pagination
  const [hasCompletedFetch, setHasCompletedFetch] = useState(false)
  const isRefreshing = (isListLoading || isPending) && items.length > 0
  const showSkeletons = items.length === 0 && !hasCompletedFetch
  const showNoResults = items.length === 0 && hasCompletedFetch && !isListLoading

  useEffect(() => {
    const request = fetchPaginatedStudies({
      page: currentPage,
      pageSize: MAX_STUDIES_ON_PAGE,
      search: searchTerm,
      sort: sortingOption,
      order: ascendingSortingOrder ? 'asc' : 'desc',
      features: selectedFilterFeatures,
    })
    if (request?.then) {
      request.finally(() => setHasCompletedFetch(true))
    }
  }, [currentPage, searchTerm, sortingOption, ascendingSortingOrder, selectedFilterFeatures, fetchPaginatedStudies])

  useEffect(() => {
    if (pagination.total_pages > 0 && currentPage > pagination.total_pages) {
      startTransition(() => setCurrentPage(pagination.total_pages))
    }
  }, [pagination.total_pages, currentPage])

  const checkIsEnrollerOrSupport = studyID => {
    const userRole = getUserRoleByStudy(user, studyID)
    return [USER_ROLES_MAP.enroller, USER_ROLES_MAP.supportAgent].includes(userRole)
  }

  const checkIsSiteUserManagementAdmin = studyID => {
    const userRole = getUserRoleByStudy(user, studyID)
    return userRole === USER_ROLES_MAP.siteUserManagementAdmin
  }

  const getMainStudyPage = studyID => {
    const isEnrollerOrSupport = checkIsEnrollerOrSupport(studyID)
    const isSiteUserManagementAdmin = checkIsSiteUserManagementAdmin(studyID)

    if (isEnrollerOrSupport) {
      return 'participants'
    }

    if (isSiteUserManagementAdmin) {
      return 'users'
    }

    return 'compliance'
  }

  const renderIcons = (studyID, { icon, text, key, extra, path }, hasStudyPromotion) => {
    const userRole = getUserRoleByStudy(user, studyID)
    const isSponsorOrResearcher =
      userRole === USER_ROLES_MAP.sponsor ||
      userRole === USER_ROLES_MAP.sponsorUserMonitor ||
      USER_ROLES_MAP.sponsorUAT ||
      USER_ROLES_MAP.sponsorDEV ||
      userRole === USER_ROLES_MAP.researcher
    const isAuditor = userRole === USER_ROLES_MAP.auditor
    const isSupport = userRole === USER_ROLES_MAP.supportAgent
    const isEnrollerOrSupport = checkIsEnrollerOrSupport(studyID)
    const mainStudyPage = getMainStudyPage(studyID)
    let historyPath = path || mainStudyPage

    switch (key) {
      case TOOLTIP_MENU_MAP.medicationAdherence:
        if (isSponsorOrResearcher || isAuditor || isSupport) historyPath = mainStudyPage
        break
      case TOOLTIP_MENU_MAP.clinro:
        if (isEnrollerOrSupport) historyPath = mainStudyPage
        break
      default:
        historyPath = path || mainStudyPage
    }

    return (
      <Fragment key={key}>
        <div
          className='icon-name'
          onClick={event => {
            event.stopPropagation()
            runStudyListNavigation({
              hasStudyPromotion,
              userPermissions: user?.permissions,
              studyId: studyID,
              fetchStudyVersionList,
              onModal: () => {
                openModal({
                  closeOnBackgroundClick: true,
                  content: <SelectStudyVersionModalContainer studyId={studyID} redirectPath={historyPath} />,
                  className: `select-study-version-modal`,
                  noButtons: true,
                })
              },
              onGo: versionId => {
                if (fetchStudy) fetchStudy(studyID)
                browserHistory.push(buildStudyRoutePath(studyID, versionId, historyPath))
              },
              onUnavailable: () => {
                openModal({
                  closeOnBackgroundClick: true,
                  content: MODAL_CONTENT_MAP.theStudyIsNotAvailableYet,
                  confirmButton: MODAL_BUTTONS_MAP.ok,
                  className: `${MODAL_CLASSES_MAP.confirmation} ${MODAL_CLASSES_MAP.uatStudyConfirmation}`,
                  hideCancelButton: true,
                  onConfirm: closeModal,
                })
              },
            })
          }}>
          <i className={icon} />
          <div className='hover-text'>{`${extra || ''} ${text}`}</div>
        </div>
      </Fragment>
    )
  }

  const updatePersistedPaginationSettings = ({ page, features, option, order }) => {
    persistStudyPaginationSettings(user?.id, {
      currentPage: page || currentPage,
      selectedFilterFeatures: features || selectedFilterFeatures,
      sortingOption: option || sortingOption,
      ascendingSortingOrder: order !== undefined ? order : ascendingSortingOrder,
    })
  }

  const updateCurrentPage = page => {
    startTransition(() => setCurrentPage(page))
    updatePersistedPaginationSettings({ page })
  }

  const updateSelectedFilterFeatures = features => {
    startTransition(() => {
      setSelectedFilterFeatures(features)
      setCurrentPage(1)
    })
    updatePersistedPaginationSettings({ features, page: 1 })
  }

  const clearSelectedFilterFeatures = () => {
    startTransition(() => {
      setSelectedFilterFeatures([])
      setCurrentPage(1)
    })
    updatePersistedPaginationSettings({ features: [], page: 1 })
  }

  const removeSelectedFilterFeature = feature => {
    const filteredFeatures = selectedFilterFeatures.filter(item => item.value !== feature.value)
    updateSelectedFilterFeatures(filteredFeatures)
  }

  const updateSortingOption = option => {
    startTransition(() => setSortingOption(option))
    updatePersistedPaginationSettings({ option })
  }

  const updateAscendingSortingOrder = order => {
    startTransition(() => setAscendingSortingOrder(order))
    updatePersistedPaginationSettings({ order })
  }

  const renderStudyCard = study => {
    const { id, config, study_duration, modules } = study
    const permissions = {
      1: 'Root',
      2: 'DCH Admin',
      14: 'DCH PII',
    }
    const userRoleId = user?.permissions?.[id]?.role_id || null
    const hasWhiteLabelingEditPermissions = userRoleId && permissions[userRoleId]
    const tooltipMenuOptions = {
      caseManagement: !!config?.case_management,
      geofencing: modules.includes('geofencing'),
      clinro: !!config?.clinro,
      medicationAdherence: !!config?.medication_adherence,
      superGems: !!config?.super_gems,
    }
    const tooltips = TOOLTIP_MENU.filter(tooltip => !!tooltipMenuOptions[tooltip.key])
    tooltips.map(tooltip => {
      if (tooltip.extra) tooltip.extra = tooltipMenuOptions[tooltip.key]
    })
    const mainStudyPage = getMainStudyPage(id)
    const dateObj = getStudyLength(study_duration)
    const dateToString = Object.keys(dateObj)
      .reduce((accVal, currVal) => accVal.concat(`${dateObj[currVal]} ${currVal}, `), '')
      .slice(0, -2)

    const onUpload = payload => {
      addPreviewLogo({
        ...payload,
        studyId: id,
      })
    }

    return (
      <StudyCard
        key={id}
        study={study}
        mainStudyPage={mainStudyPage}
        hasWhiteLabelingEditPermissions={hasWhiteLabelingEditPermissions}
        onUpload={onUpload}
        uploadLogo={uploadLogo}
        openModal={openModal}
        closeModal={closeModal}
        deleteLogo={deleteLogo}
        dateToString={dateToString}
        tooltips={tooltips}
        renderIcons={renderIcons}
        user={user}
        fetchStudyVersionList={fetchStudyVersionList}
        fetchStudy={fetchStudy}
        studyVersionList={studyVersionList}
      />
    )
  }

  return (
    <div className='studies page'>
      <div className='flexed-header'>
        <h3>All Studies</h3>
        {allowCreateStudy && (
          <Button
            id='add-new-study-top'
            content='Add Study'
            onClick={() => {
              resetStudyData(isRootOrDchAdmin(user))
              browserHistory.push(CREATE_STUDY_FULL_URLS.type)
            }}
          />
        )}
      </div>
      <div className='studies-list-wrapper'>
        <div className='flexed center-aligned space-between-justified'>
          <SearchInput id='search-study-input' useDebounce performExternalUpdate={() => updateCurrentPage(1)} />
          <div className='flexed sorting-filter-section'>
            <StudySorting
              sortingOption={sortingOption}
              updateAscendingSortingOrder={updateAscendingSortingOrder}
              ascendingSortingOrder={ascendingSortingOrder}
              updateSortingOption={updateSortingOption}
            />

            <StudyFeatureFilter
              selectedFilterFeatures={selectedFilterFeatures}
              setSelectedFilterFeatures={updateSelectedFilterFeatures}
              clearSelectedFilterFeatures={clearSelectedFilterFeatures}
            />
          </div>
        </div>
        {selectedFilterFeatures.length ? (
          <div className='filter-feature-list'>
            {selectedFilterFeatures.map(feature => (
              <div
                key={feature.key}
                className='filter-feature-item'
                onClick={() => removeSelectedFilterFeature(feature)}>
                {feature.text}
                <i className='fas fa-times' />
              </div>
            ))}
          </div>
        ) : null}
        <div className={`studies-list${isRefreshing ? ' is-refreshing' : ''}`}>
          {showSkeletons &&
            Array.from({ length: MAX_STUDIES_ON_PAGE }).map((_, index) => (
              <div key={`study-skeleton-${index}`} className='study-box study-card-skeleton' />
            ))}
          {!showSkeletons && items.length
            ? items.map(renderStudyCard)
            : showNoResults && (
                <div className='no-results-section'>
                  <img src={searchImg} />
                  <div className='no-results-title'>{STRINGS.noResults}</div>
                  <div className='no-results-subtitle'>{STRINGS.checkTheSpelling}</div>
                </div>
              )}
          {isRefreshing && (
            <div className='studies-list-overlay'>
              <Loader inContainer size={40} />
            </div>
          )}
          {showSkeletons && (
            <div className='studies-list-overlay'>
              <Loader inContainer size={40} />
            </div>
          )}
        </div>
        <StudiesPaginationPanel
          currentPage={currentPage}
          setCurrentPage={updateCurrentPage}
          totalPages={pagination.total_pages || 0}
        />
      </div>
    </div>
  )
}

StudiesList.propTypes = {
  studiesPage: PropTypes.object,
  resetStudy: PropTypes.func,
  resetStudyData: PropTypes.func,
  deleteStudy: PropTypes.func,
  allowCreateStudy: PropTypes.bool,
  user: PropTypes.object,
  searchTerm: PropTypes.string,
  fetchPaginatedStudies: PropTypes.func,
  isListLoading: PropTypes.bool,
}

export default StudiesList
